import { useContext, useEffect, useRef, useState } from "react";

import { handleMessageError, handleMessageSuccess, handleMessageWarning } from "../../../../components/toast";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { schema, schemaDefaultValues } from "./schema";
import LibBoolean from "../../../../util/libBool";

import { ContextGlobal } from "../../../../context";
import reqEmpresa from "../../../../services/reqMultiTenant/reqEmpresa";
import reqConfiguracaoCatalogo from "../../../../services/reqMultiTenant/reqConfiguracaoCatalogo";
import { getReturnUpdated } from "../../../../util/libObject";
import { useAddress } from "../../../../hook/useAddress";
import { setSessionUfAndCity } from "../../../../util/libLocalStorage";

export const IndexHook = () => {
	const {
		register,
		handleSubmit,
		setValue,
		setFocus,
		getValues,
		trigger,
		setError,
		reset,
		watch,
		control,
		formState: { errors, isSubmitting },
	} = useForm({
		mode: "onSubmit",
		//quando revalidar os campos
		resolver: zodResolver(schema),
		defaultValues: schemaDefaultValues,
	});

	const { brazilStates, fetchCitiesByUf, loadingCities } = useContext(ContextGlobal);
	const [loading, setLoading] = useState(true);
	const [citiesData, setCitiesData] = useState([]);
	const [shouldOpenForm, setShouldOpenForm] = useState(false);
	const [isUpdatedImage, setIsUpdatedImage] = useState(false);
	const url_logo = watch("url_logo");
	const id_empresa = watch("id_empresa");
	const url_background = watch("url_background");

	// Controle para detectar alterações na aba Catálogo
	const catalogoInitialRef = useRef(null);

	//#region Fetch Empresa Data
	const fetchEmpresaData = async () => {
		try {
			setLoading(true);
			const response = await reqEmpresa.listID();
			const storeData = getReturnUpdated(response);

			const ativoValue = storeData.ativo === "SIM"; // Converte "SIM" para true, "NAO" para false
			storeData.ativo = ativoValue;

			Object.keys(storeData).forEach((field) => {
				if (field !== "id_cidade") setValue(field, storeData[field]);
			});

			await handleFetchCitiesByUf(storeData.uf);

			// seta a cidade DEPOIS das opções estarem carregadas
			setValue("id_cidade", storeData.id_cidade);

			// Busca os dados do catálogo separadamente
			await fetchCatalogData();

			setShouldOpenForm(true);
		} catch (error) {
			setShouldOpenForm(false);
			handleMessageWarning(error.message);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchEmpresaData(); // Chama a função de busca de dados
	}, []); //

	//#endregion

	//#region Fetch Catalogo Data
	const fetchCatalogData = async () => {
		try {
			const response = await reqConfiguracaoCatalogo.listByEmpresa();
			const catalogData = getReturnUpdated(response);

			if (catalogData && catalogData.id_configuracao_catalogo) {
				const camposCatalogo = [
					"id_configuracao_catalogo",
					"theme",
					"entrega_transportadora",
					"entrega_local",
					"complementos",
					"validar_estoque",
					"titulo_seo",
					"descricao_seo",
				];

				camposCatalogo.forEach((field) => {
					if (catalogData[field] !== undefined) {
						setValue(field, catalogData[field]);
					}
				});

				// Salva o estado inicial para comparação posterior
				catalogoInitialRef.current = {
					id_configuracao_catalogo: catalogData.id_configuracao_catalogo ?? 0,
					theme: catalogData.theme,
					entrega_transportadora: catalogData.entrega_transportadora,
					entrega_local: catalogData.entrega_local,
					complementos: catalogData.complementos,
					validar_estoque: catalogData.validar_estoque,
					titulo_seo: catalogData.titulo_seo ?? "",
					descricao_seo: catalogData.descricao_seo ?? "",
				};
			} else {
				// Nenhum registro ainda — estado inicial zerado
				catalogoInitialRef.current = { id_configuracao_catalogo: 0 };
			}
		} catch {
			// catálogo ainda não cadastrado, ignora silenciosamente
			catalogoInitialRef.current = { id_configuracao_catalogo: 0 };
		}
	};
	//#endregion

	//#region Buscar Cidades pelo estado
	const handleFetchCitiesByUf = async (uf) => {
		try {
			const responseCities = await fetchCitiesByUf(uf); // Aqui vai a função para buscar as cidades

			setCitiesData(responseCities);
		} catch (error) {
			handleMessageWarning(error.message);
		}
	};
	//#endregion

	//#region Selecionar estado e buscar as cidades
	const handleSelectedState = async (selectedOption) => {
		// quando selecionar o estado no input chama essa função
		await handleFetchCitiesByUf(selectedOption.value);
		setValue("id_cidade", "");
	};
	//#endregion

	//#region Pesquisar e preenche o endereço pelo cep
	const { handleFetchAddressByCep, cepIsSubmitting } = useAddress(setValue, setFocus, getValues);

	const handleFetchAddress = async () => {
		try {
			const uf = await handleFetchAddressByCep();
			handleFetchCitiesByUf(uf);
		} catch (error) {
			handleMessageWarning(error.message);
		}
	};
	//#endregion

	//#region onSubmit
	const handleOnSubmit = async (data) => {
		try {
			const newData = await ValidateSubmit(data);

			// Atualiza o valor no input para refletir o que foi salvo
			setValue("fantasia", newData.fantasia);

			// Salva dados da empresa (tabs 0 e 1)
			await reqEmpresa.update(newData);

			// Verifica se a aba Catálogo foi alterada para salvar separadamente
			if (catalogoFoiAlterado(data)) {
				const catalogoData = {
					id_configuracao_catalogo: catalogoInitialRef.current?.id_configuracao_catalogo ?? 0,
					theme: data.theme,
					entrega_transportadora: data.entrega_transportadora,
					entrega_local: data.entrega_local,
					complementos: data.complementos,
					validar_estoque: data.validar_estoque,
					titulo_seo: data.titulo_seo ?? "",
					descricao_seo: data.descricao_seo ?? "",
				};
				const result = await reqConfiguracaoCatalogo.insertUpdate(catalogoData);
				// Atualiza o id gerado caso tenha sido INSERT
				const returned = getReturnUpdated(result);
				if (returned?.id_configuracao_catalogo) {
					catalogoInitialRef.current = {
						...catalogoData,
						id_configuracao_catalogo: returned.id_configuracao_catalogo,
					};
					setValue("id_configuracao_catalogo", returned.id_configuracao_catalogo);
				} else {
					catalogoInitialRef.current = { ...catalogoData };
				}
			}

			handleMessageSuccess("Registro salvo com sucesso.");

			setSessionUfAndCity(newData.uf, newData.id_cidade);

			setIsUpdatedImage(false);
		} catch (error) {
			if (error.field) {
				errosAfterPost(error.field, error.message);
			} else handleMessageError(error.message);
		}
	};

	const errosAfterPost = (field, message) => {
		setError(field?.toLowerCase(), { type: "manual", message: message });
	};

	const ValidateSubmit = async (data) => {
		if (data.id_empresa === 0) {
			throw new Error("Identificador da empresa não encontrado.");
		}

		const newAtivo = LibBoolean.booleanToStr(data.ativo);
		const newData = {
			...data,
			fantasia: data.fantasia && data.fantasia.trim() !== "" ? data.fantasia : data.nome,
			ativo: newAtivo,
		};
		return newData;
	};

	const catalogoFoiAlterado = (data) => {
		const initial = catalogoInitialRef.current;
		if (!initial) return true; // nunca buscou, salva
		return (
			data.theme !== initial.theme ||
			data.entrega_transportadora !== initial.entrega_transportadora ||
			data.entrega_local !== initial.entrega_local ||
			data.complementos !== initial.complementos ||
			data.validar_estoque !== initial.validar_estoque ||
			(data.titulo_seo ?? "") !== (initial.titulo_seo ?? "") ||
			(data.descricao_seo ?? "") !== (initial.descricao_seo ?? "")
		);
	};
	//#endregion

	//#region Tabs

	const [valueTab, setValueTab] = useState("0");

	const handleChangeTab = async (event, newValue) => {
		event.preventDefault();

		const isValid = await trigger();

		if (isValid) setValueTab(newValue);
	};
	//#endregion

	return {
		//hooks
		handleFetchAddress,
		handleSelectedState,
		handleOnSubmit,

		//useForm
		setValue,
		setFocus,
		register,
		handleSubmit,
		control,
		errors,
		isSubmitting,
		watch,

		//States
		shouldOpenForm,
		citiesData,
		brazilStates,

		//watch
		url_logo,
		url_background,
		id_empresa,

		//Loading
		cepIsSubmitting,
		loading,
		loadingCities,

		//vars
		isUpdatedImage,
		setIsUpdatedImage,

		//tab
		valueTab,
		handleChangeTab,
	};
};
